import { motion } from 'framer-motion';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BespokePage = () => {
  const categories = [
    {
      title: 'Corporate Uniforms',
      image: 'https://images.unsplash.com/photo-1758520144424-2e04f4ad7f60?crop=entropy&cs=srgb&fm=jpg&q=85',
      useCases: ['Office Staff', 'Executive Teams', 'Security Personnel', 'Hospitality Staff'],
      fabrics: ['Cotton Blends', 'Polyester', 'Linen', 'Premium Wool'],
      customization: ['Logo Embroidery', 'Color Matching', 'Fit Adjustments', 'Accessory Integration'],
    },
    {
      title: 'School Uniforms',
      image: 'https://images.unsplash.com/photo-1758518730523-c9f6336ebdae?crop=entropy&cs=srgb&fm=jpg&q=85',
      useCases: ['Primary Schools', 'High Schools', 'Colleges', 'Sports Teams'],
      fabrics: ['Durable Cotton', 'Easy-Care Blends', 'Breathable Fabrics', 'Stain-Resistant Materials'],
      customization: ['School Crest', 'House Colors', 'ID Labels', 'Growth Allowance'],
    },
    {
      title: 'Industrial & Staff Wear',
      image: 'https://images.unsplash.com/photo-1684259499086-93cb3e555803?crop=entropy&cs=srgb&fm=jpg&q=85',
      useCases: ['Factory Workers', 'Warehouse Staff', 'Maintenance Teams', 'Technical Staff'],
      fabrics: ['Heavy-Duty Cotton', 'Flame-Resistant', 'High-Visibility', 'Denim Blends'],
      customization: ['Safety Features', 'Pocket Configurations', 'Reflective Strips', 'Reinforced Seams'],
    },
    {
      title: 'Sports Teams & Athletics',
      image: 'https://images.unsplash.com/photo-1752778598489-ae34ac74ab34?crop=entropy&cs=srgb&fm=jpg&q=85',
      useCases: ['Cricket Teams', 'Football Clubs', 'Corporate Sports', 'Marathon Events'],
      fabrics: ['Moisture-Wicking', 'Stretchable Polyester', 'Quick-Dry', 'Lightweight Mesh'],
      customization: ['Team Names', 'Player Numbers', 'Sponsor Logos', 'Sublimation Printing'],
    },
  ];

  const whatsappNumber = '+919619977421';
  const whatsappMessage = encodeURIComponent('Hi! I\'m interested in bespoke uniform manufacturing.');

  return (
    <div className="min-h-screen pt-20" data-testid="bespoke-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="bespoke-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="bespoke-hero-heading">
              Bespoke Uniform Manufacturing
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed mb-8">
              From corporate offices to sports fields, we manufacture custom uniforms that reflect your brand identity and meet institutional standards. No pricing, no cart—just consultation-led excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="request-quote-btn"
                className="inline-flex items-center justify-center px-8 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors"
              >
                Request Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-enquiry-btn"
                className="inline-flex items-center justify-center px-8 py-3 border-2 border-brand-pearl text-brand-pearl font-medium rounded-sm hover:bg-brand-pearl hover:text-brand-ivory transition-colors"
              >
                <MessageSquare className="mr-2 w-5 h-5" />
                WhatsApp Enquiry
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((category, idx) => (
        <section
          key={idx}
          className={`section-spacing ${idx % 2 === 0 ? 'bg-brand-ivory' : 'bg-brand-timberwolf'}`}
          data-testid={`bespoke-category-${idx}`}
        >
          <div className="container-custom">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={idx % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="parallax-img rounded-sm overflow-hidden border border-brand-pearl/10">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-96 object-cover"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={idx % 2 === 1 ? 'lg:order-1' : ''}
              >
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-6">
                  {category.title}
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-brand-pearl mb-3">Use Cases</h3>
                    <ul className="grid grid-cols-2 gap-2">
                      {category.useCases.map((useCase, i) => (
                        <li key={i} className="text-sm text-brand-walnut flex items-center">
                          <span className="w-1.5 h-1.5 bg-brand-pearl rounded-full mr-2" />
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-heading font-semibold text-brand-pearl mb-3">Fabric Options</h3>
                    <div className="flex flex-wrap gap-2">
                      {category.fabrics.map((fabric, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-brand-timberwolf text-brand-walnut text-xs font-medium rounded-sm border border-brand-pearl/10"
                        >
                          {fabric}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-heading font-semibold text-brand-pearl mb-3">Customization Scope</h3>
                    <ul className="grid grid-cols-2 gap-2">
                      {category.customization.map((custom, i) => (
                        <li key={i} className="text-sm text-brand-walnut flex items-center">
                          <span className="w-1.5 h-1.5 bg-brand-green rounded-full mr-2" />
                          {custom}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="section-spacing bg-brand-pearl text-brand-ivory" data-testid="bespoke-cta">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
            Ready to Start Your Bespoke Project?
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto mb-8 opacity-90">
            Our team is ready to discuss your requirements and provide a detailed consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center justify-center px-8 py-3 bg-brand-ivory text-brand-pearl font-medium rounded-sm hover:bg-brand-timberwolf transition-colors"
            >
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/process"
              data-testid="cta-process-btn"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-brand-ivory text-brand-ivory font-medium rounded-sm hover:bg-brand-ivory/10 transition-colors"
            >
              View Our Process
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BespokePage;